import * as React from 'react';
import { Link, useRouteContext } from '@tanstack/react-router';
import type { LinkComponentProps } from '@tanstack/react-router';
import { AppUiProvider } from '@ooh/screens';
import type { ApiClient, ScreenLinkProps } from '@ooh/screens';
import { ApiError, callEndpoint } from '@ooh/contracts';
import type { z } from 'zod';
import type { EndpointDef } from '@ooh/contracts';
import type { RouterContext } from '../routes/__root';
import { endpointBaseUrl } from '../lib/auth-client';
import { addActionBreadcrumb } from '../lib/sentry';

// The plug contract carries `to` as a plain string, so a query string rides along in it. The router
// takes it as a typed search object, with numeric values as numbers so the URL stays unquoted.
export function splitLinkTarget(target: string): {
  to: string;
  search?: Record<string, string | number>;
} {
  const queryStart = target.indexOf('?');
  if (queryStart === -1) return { to: target };
  const search = Object.fromEntries(
    Array.from(new URLSearchParams(target.slice(queryStart + 1)), ([key, value]) => [
      key,
      /^\d+$/.test(value) ? Number(value) : value,
    ]),
  );
  return { to: target.slice(0, queryStart), search };
}

function RouterLink({ to, search, ...rest }: ScreenLinkProps) {
  // The registered router types `to` and `search` per route.
  const linkProps = {
    ...splitLinkTarget(to),
    ...(search && { search }),
    ...rest,
  } as unknown as LinkComponentProps<'a'>;
  return <Link {...linkProps} />;
}

function uploadUrl(path: string, pathParams: Record<string, string | number> = {}): string {
  const filled = path.replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, (match, name: string) => {
    const value = pathParams[name];
    return value === undefined ? match : encodeURIComponent(String(value));
  });
  return `${endpointBaseUrl(path)}${filled}`;
}

export async function uploadFile<TDef extends EndpointDef>(
  def: TDef,
  file: File,
  accessToken: string | null,
  fields: Record<string, string> = {},
  pathParams: Record<string, string | number> = {},
): Promise<z.output<TDef['responseSchema']>> {
  const formData = new FormData();
  formData.append('file', file);
  for (const [name, value] of Object.entries(fields)) formData.append(name, value);
  // eslint-disable-next-line no-restricted-globals -- multipart/form-data requires raw fetch; callEndpoint does not support FormData
  const response = await fetch(uploadUrl(def.path, pathParams), {
    method: def.method,
    body: formData,
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  const body: unknown = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(response.status, response.statusText, body);
  return def.responseSchema.parse(body) as z.output<TDef['responseSchema']>;
}

export async function downloadFile(
  def: EndpointDef,
  query: Record<string, string>,
  accessToken: string | null,
): Promise<Blob> {
  const search = new URLSearchParams(query).toString();
  // eslint-disable-next-line no-restricted-globals -- file download requires raw fetch; callEndpoint does not support binary/blob responses
  const response = await fetch(`${uploadUrl(def.path)}${search === '' ? '' : `?${search}`}`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    signal: AbortSignal.timeout(30_000),
  });
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => ({}));
    throw new ApiError(response.status, response.statusText, body);
  }
  return response.blob();
}

// Anonymous public-link endpoints: no bearer token, and no 401 refresh (there is no session to renew).
export const callPublicEndpoint: ApiClient['callPublic'] = (def, request, options) =>
  callEndpoint(def, request, { baseUrl: endpointBaseUrl(def.path), signal: options?.signal });

export function ScreensProvider({ children }: { children: React.ReactNode }) {
  const { auth, queryClient } = useRouteContext({ from: '__root__' }) as RouterContext;
  const api = React.useMemo<ApiClient>(
    () => ({
      call: auth.callWithAuth,
      callPublic: callPublicEndpoint,
      upload: (def, file, fields, pathParams) =>
        uploadFile(def, file, auth.accessToken, fields, pathParams),
      download: (def, query = {}) => downloadFile(def, query, auth.accessToken),
    }),
    [auth],
  );
  const { user, permissions } = auth;
  const currentUser = React.useMemo(() => ({ user, permissions }), [user, permissions]);

  return (
    <AppUiProvider
      api={api}
      link={RouterLink}
      currentUser={currentUser}
      queryClient={queryClient}
      onActionBreadcrumb={addActionBreadcrumb}
    >
      {children}
    </AppUiProvider>
  );
}
